/**
 * Floating chat widget for the Veridex AI Assistant.
 *
 * - Single round-trip per question (no streaming) to minimise token cost.
 * - sessionStorage cache keyed by lowercased question → same question never
 *   hits the backend twice within the tab's lifetime.
 * - Hidden on /login, /register, /pilot-qualification, /pilot-request and
 *   anything under /legal so it doesn't hover above onboarding funnels.
 */
import { useState, useEffect, useRef } from 'react';
import { useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { MessageCircle, X, Send, Loader2, Sparkles, Zap } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CACHE_PREFIX = 'veridex:ai-cache:';
const MAX_HISTORY = 12;

const HIDDEN_ROUTES = [
  '/login', '/register', '/forgot-password', '/reset-password',
  '/pilot-qualification', '/pilot-request', '/pilot/success', '/pilot/cancel',
];

function normalise(q) {
  return q.trim().toLowerCase().replace(/\s+/g, ' ');
}

function getCached(q) {
  try {
    const raw = sessionStorage.getItem(CACHE_PREFIX + normalise(q));
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function setCached(q, value) {
  try {
    sessionStorage.setItem(CACHE_PREFIX + normalise(q), JSON.stringify(value));
  } catch { /* noop */ }
}

export default function ChatWidget() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]); // [{q, a, cached}]
  const [usage, setUsage] = useState(null); // { tier, unlimited, remaining, limit }
  const scrollRef = useRef(null);

  // Fetch initial usage the first time the panel is opened.
  useEffect(() => {
    if (!open || usage) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/ai/usage`, { withCredentials: true });
        setUsage(r.data);
      } catch { /* silent — anonymous users just don't see the badge */ }
    })();
  }, [open, usage]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [history, open]);

  const hidden =
    HIDDEN_ROUTES.some((r) => location.pathname.startsWith(r)) ||
    location.pathname.startsWith('/legal');

  if (hidden) return null;

  const submit = async (e) => {
    e?.preventDefault?.();
    const question = q.trim();
    if (!question || sending) return;

    // Cache hit → no network call
    const cached = getCached(question);
    if (cached?.answer) {
      setHistory((h) => [...h, { q: question, a: cached.answer, cached: true }].slice(-MAX_HISTORY));
      setQ('');
      return;
    }

    setSending(true);
    try {
      const res = await axios.post(`${API}/ai/ask`, { question });
      const answer = res.data?.answer || 'No answer.';
      setCached(question, { answer });
      setHistory((h) => [...h, { q: question, a: answer, cached: false }].slice(-MAX_HISTORY));
      setQ('');
      // Refresh usage badge with the number returned by the server.
      if (typeof res.data?.remaining_queries === 'number' || res.data?.unlimited) {
        setUsage((u) => ({
          ...(u || {}),
          tier: res.data.tier,
          unlimited: !!res.data.unlimited,
          remaining: res.data.remaining_queries,
          limit: res.data.unlimited ? null : (u?.limit ?? 10),
          used: res.data.unlimited
            ? (u?.used ?? 0)
            : Math.max(0, (u?.limit ?? 10) - (res.data.remaining_queries ?? 0)),
        }));
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || 'AI assistant is unavailable right now.';
      setHistory((h) => [...h, { q: question, a: `⚠️ ${detail}`, error: true }].slice(-MAX_HISTORY));
      setQ('');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="ai-chat-open-btn"
          className="fixed bottom-6 right-6 z-[60] flex items-center gap-2 rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30 px-4 py-3 hover:scale-105 transition-transform"
          aria-label="Open Veridex AI"
        >
          <MessageCircle className="w-4 h-4" />
          <span className="text-sm font-medium">Ask Veridex AI</span>
        </button>
      )}

      {open && (
        <div
          className="fixed bottom-6 right-6 z-[60] w-[92vw] max-w-sm rounded-xl border border-border bg-card shadow-2xl shadow-primary/10 flex flex-col overflow-hidden"
          style={{ maxHeight: '72vh' }}
          data-testid="ai-chat-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-primary text-primary-foreground">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              <span className="text-sm font-semibold">Veridex AI</span>
              <span className="text-[10px] opacity-80 uppercase tracking-wider">gemini-2.5-flash</span>
            </div>
            <div className="flex items-center gap-2">
              {usage?.unlimited && (
                <Badge className="bg-white/20 text-white border-white/30 hover:bg-white/20 text-[10px] px-2 py-0" data-testid="ai-usage-unlimited">
                  <Zap className="w-3 h-3 mr-0.5" /> Unlimited
                </Badge>
              )}
              {usage && !usage.unlimited && typeof usage.remaining === 'number' && (
                <Badge
                  className={`text-[10px] px-2 py-0 border-white/30 ${usage.remaining <= 2 ? 'bg-amber-400 text-amber-900' : 'bg-white/20 text-white hover:bg-white/20'}`}
                  data-testid="ai-usage-remaining"
                >
                  {usage.remaining}/{usage.limit} left
                </Badge>
              )}
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="ai-chat-close-btn"
                className="p-1 rounded hover:bg-white/10"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          {usage && !usage.unlimited && usage.remaining === 0 && (
            <div className="px-4 py-2 text-xs bg-amber-50 dark:bg-amber-950/30 text-amber-900 dark:text-amber-200 border-b border-amber-200/50 flex items-center justify-between gap-2" data-testid="ai-usage-exhausted">
              <span>Monthly free limit reached.</span>
              <Link to="/pricing" className="font-semibold underline">Upgrade →</Link>
            </div>
          )}

          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-3 py-3 space-y-3 bg-muted/20"
            data-testid="ai-chat-history"
          >
            {history.length === 0 && (
              <div className="text-xs text-muted-foreground px-2 py-4 text-center">
                Ask me anything about PCF, CBAM, supplier data or your compliance status.
                I answer in 2-3 sentences, off-topic questions are politely declined.
              </div>
            )}
            {history.map((m, i) => (
              <div key={i} className="space-y-1" data-testid={`ai-chat-msg-${i}`}>
                <div className="flex justify-end">
                  <div className="max-w-[85%] bg-primary text-primary-foreground rounded-lg rounded-br-sm px-3 py-2 text-sm">
                    {m.q}
                  </div>
                </div>
                <div className="flex justify-start">
                  <div className={`max-w-[85%] rounded-lg rounded-bl-sm px-3 py-2 text-sm ${m.error ? 'bg-red-50 text-red-800 border border-red-200' : 'bg-card border border-border'}`}>
                    {m.a}
                    {m.cached && (
                      <div className="text-[10px] text-muted-foreground mt-1">cached</div>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground px-2" data-testid="ai-chat-loading">
                <Loader2 className="w-3 h-3 animate-spin" /> Thinking…
              </div>
            )}
          </div>

          <form onSubmit={submit} className="flex items-center gap-2 p-2 border-t border-border bg-card">
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Ask about PCF, CBAM, suppliers…"
              maxLength={500}
              disabled={sending}
              data-testid="ai-chat-input"
              className="flex-1 text-sm"
            />
            <Button
              type="submit"
              size="icon"
              disabled={sending || !q.trim()}
              data-testid="ai-chat-send-btn"
              className="shrink-0 bg-primary hover:bg-primary/90"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </form>
        </div>
      )}
    </>
  );
}
